import smartcrop from 'smartcrop';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { uploadImageByImageInfo } from '../../ai/core/api/api';
import { SearchImageInfo } from '@src/slide/application/service/ai/core/api/api.interface';
import { traverseTags } from '@tencent/slide-markdown-converter/src/schema';
import { getShapeTextRun } from '../../ai-ssistant/ai-generator/data-utils';
import { DataType, IPictureDataModel, IShapeDataModel } from '@tencent/slide-sdk-model';
import { replaceSingleImage } from '../../copy-paste/core/cp-image';
import { ImageReplaceAction } from '../../copy-paste/core/cp-image.new/image-edit-manager/core/image-edit-manager.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { transaction } from '@src/slide/domain/slide-app';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

interface IUploadResult {
    url: string;
    width: number;
    height: number;
}

interface IReplaceImageInfo {
    src: string;
    width: number;
    height: number;
    srcRect?: unknown;
}

export class ReplaceImageUtils {
    /**
     * 搜索结果/AI生成的图片，先在后台上传，再替换原有图片
     *
     * @param models 需要替换图片的原始 uiModel 列表
     * @param searchInfo 替换目标图片信息
     * @param isAIGenerate 是否是AI生成的图片
     * @param onError 出错回调
     */
    public static async replaceImagesWithSearchInfo(
        models: IElementBaseModel[],
        searchInfo: SearchImageInfo,
        isAIGenerate: boolean,
        onError?: () => void,
    ) {
        // await 上传新图片
        const uploadResult = (await uploadImageByImageInfo({ ...searchInfo }))?.upload_image;
        if (!uploadResult) {
            onError?.();
            return;
        }
        // await 计算新图片在旧图位的位置信息
        const replaceImageInfos = await Promise.all(models.map((model) => {
            const selfModel = model as IPictureModel | IShapeModel;
            return this.getUploadImageInfo(selfModel, uploadResult, !isAIGenerate);
        })).catch(() => {
            onError?.();
            return;
        });
        // 替换更新图片
        transaction(() => {
            models.forEach((model, index) => {
                if (model?.type === UIModelType.IMAGE || model?.type === UIModelType.SHAPE) {
                    this.replaceSingleImageWithSearchInfo(
                        model as IPictureModel | IShapeModel,
                        searchInfo,
                        replaceImageInfos[index],
                        isAIGenerate,
                    );
                }
            });
        });
    }

    // 获取此页面AI生成的标题
    public static getPageTitle(slide?: ISlideModel): string | undefined {
        const shapeList = slide?.sdkData?.shapeTree?.getWritableOwnedData().shapeList;
        if (!shapeList) return;

        const { idMap } = traverseTags(shapeList);
        if (!idMap.title) return;

        const titleModel = slide?.sdkData?.getShapeById(idMap.title);
        if (titleModel?.type !== DataType.Shape) return;

        return getShapeTextRun(titleModel);
    }

    // 获取此页面AI生成的标题和副标题
    public static getPageTitleList(slide?: ISlideModel): string[] {
        const shapeList = slide?.sdkData?.shapeTree?.getWritableOwnedData().shapeList;
        if (!shapeList) return [];

        const { idMap } = traverseTags(shapeList);

        const result: string[] = [];
        idMap.list?.forEach((item) => {
            if (!item.title) return;
            const model = slide?.sdkData?.getShapeById(item.title);
            if (model?.type !== DataType.Shape) return;
            result.push(getShapeTextRun(model));
        });

        return result;
    }

    // 获取所有的 AI 图片
    public static getAllAiPictures() {
        const images: { slideId: string; shapeId: string; url: string }[] = [];
        SlideDataServices.SDKDataServices.PresentationStoreService()
            ?.getPresentation()
            .slideList.forEach((slide) => {
                slide.forEachDescendant((shape) => {
                    if (shape.type === DataType.Picture) {
                        const desc = (shape as IPictureDataModel).getDescription();
                        if (desc?.type === 'image') {
                            images.push({
                                slideId: slide.id,
                                shapeId: shape.id,
                                url: (shape as IPictureDataModel).props.blipFill?.blip?.embed ?? '',
                            });
                        }
                    } else if (shape.type === DataType.Shape) {
                        const desc = (shape as IShapeDataModel).getDescription();
                        if (desc?.type === 'image') {
                            images.push({
                                slideId: slide.id,
                                shapeId: shape.id,
                                url: (shape as IShapeDataModel).props.spPr.blipFill?.blip?.embed ?? '',
                            });
                        }
                    }
                });
            });
        return images;
    }

    /**
     * 用搜索结果/AI生成的单张图片替换原有图片
     *
     * 图片事先已经在后台上传为内链，因此不用再进行上传，可以直接替换
     * 搜图替换还需要存储图片的原始来源地址
     *
     * @param model 需要替换图片的原始 uiModel
     * @param searchInfo 替换的目标图片信息
     * @param replaceImageInfo 上传好的图片地址
     * @param isAIGenerate 是否是 AI 生成的图片
     */
    private static async replaceSingleImageWithSearchInfo(
        model: IPictureModel | IShapeModel,
        searchInfo: SearchImageInfo,
        replaceImageInfo: IReplaceImageInfo,
        isAIGenerate: boolean,
    ) {
        const descr = model.sdkData?.getDescription();

        transaction(() => {
            // 替换图片
            const isImage = model.type === UIModelType.IMAGE;
            const replaceAction = isImage ? ImageReplaceAction.foreground : ImageReplaceAction.background;
            replaceSingleImage([replaceImageInfo], model, replaceAction);

            // 更新标签信息
            const newDesc = isAIGenerate ? { ...descr } : { ...descr, sourceUrl: searchInfo.source_url };
            model.updateDescription(newDesc);
        });
    }

    private static async getUploadImageInfo(
        model: IPictureModel | IShapeModel,
        uploadResult: IUploadResult,
        useSmartCrop: boolean,
    ): Promise<IReplaceImageInfo> {
        const { url: src, width, height } = uploadResult;
        if (useSmartCrop) {
            // 智能裁剪
            const cropImage = new Image();
            const cropUrl = new URL(uploadResult.url);
            cropImage.src = `${cropUrl.protocol}//${cropUrl.hostname}${cropUrl.pathname}?imageView2/2/w/400/h/400`;
            cropImage.crossOrigin = 'Anonymous';
            return new Promise((resolve) => {
                cropImage.onload = async () => {
                    const { width: frameWidth, height: frameHeight } = model.props.style;
                    const { topCrop } = await smartcrop.crop(cropImage, { width: frameWidth, height: frameHeight });
                    return resolve({
                        src,
                        width,
                        height,
                        srcRect: {
                            l: topCrop.x / cropImage.width,
                            r: (cropImage.width - topCrop.width - topCrop.x) / cropImage.width,
                            t: topCrop.y / cropImage.height,
                            b: (cropImage.height - topCrop.height - topCrop.y) / cropImage.height,
                        },
                    });
                };
            });
        }
        return { src, width, height };
    }
}
